import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Quote } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";

const clients = [
  { logo: "/assets/clients/ananda-homes.png", name: "Ananda Homes", sector: "Real Estate", testId: "client-ananda-homes" },
  { logo: "/assets/clients/muppa.png", name: "Muppa", sector: "Real Estate", testId: "client-muppa" },
  { logo: "/assets/clients/cmm.png", name: "Centre for Migration Medicine", sector: "Healthcare", testId: "client-cmm" },
  { logo: "/assets/clients/niharika.png", name: "Niharika", sector: "Real Estate", testId: "client-niharika" },
  { logo: "/assets/clients/riddhi.png", name: "Riddhi Builders & Developers", sector: "Construction", testId: "client-riddhi" },
  { logo: "/assets/clients/marvel-infra.png", name: "Marvel Infra Project", sector: "Engineers & Contractors", testId: "client-marvel-infra" },
  { logo: "/assets/clients/hdfc-bank.png", name: "HDFC Bank", sector: "Banking & Finance", testId: "client-hdfc" },
  { logo: "/assets/clients/aryamitra.png", name: "Aryamitra Projects", sector: "Real Estate", testId: "client-aryamitra" },
  { logo: "/assets/clients/shangrila.png", name: "Shangrila", sector: "Residential Towers", testId: "client-shangrila" },
  { logo: "/assets/clients/sri-aditya.png", name: "Sri Aditya Homes", sector: "Real Estate", testId: "client-sri-aditya" },
  { logo: "/assets/clients/wild-wings.png", name: "Wild Wings", sector: "Premium Hotel", testId: "client-wild-wings" },
  { logo: "/assets/clients/vjit.png", name: "Vidya Jyothi Institute of Technology", sector: "Education", testId: "client-vjit" },
  { logo: "/assets/clients/prasad-hospitals.png", name: "Prasad Hospitals", sector: "Healthcare", testId: "client-prasad" },
];

const testimonials = [
  {
    quote: "We hired STARLINK for our housing society about six months ago. The difference was noticeable immediately. The guards are always in uniform, polite to residents, but strict with delivery logs and visitor entry. We finally have peace of mind regarding gate security, especially during the night shifts.",
    name: "Rajesh Kumar",
    role: "Society Secretary",
  },
  {
    quote: "We had a minor security incident at our warehouse last year, and the way the STARLINK guard handled the situation prevented a major theft. He followed protocol perfectly and alerted the authorities immediately. Highly recommended for industrial sites.",
    name: "Vikram Singh",
    role: "Operations Head",
  },
  {
    quote: "I hired STARLINK for our annual corporate awards night. We needed security that looked professional and could blend in with a high-profile crowd. They managed the guest list verification and VIP movement flawlessly. I didn't have to worry about a thing all evening.",
    name: "Meera Kapoor",
    role: "VP of Communications",
  },
  {
    quote: "We switched our housekeeping contract to STARLINK Security Force recently. Their staff is well-trained in using the right chemicals and equipment. Our office floors and washrooms have never looked cleaner. It's nice to walk into a fresh-smelling office every morning.",
    name: "Priya Menon",
    role: "HR Director",
  },
];

export default function Clients() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setActive((a) => (a + 1) % testimonials.length), 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <section className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
          <Reveal>
            <Eyebrow>Our Clients</Eyebrow>
            <h1
              className="mt-5 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-slate-900"
              data-testid="clients-headline"
            >
              Trusted by names that <span className="italic text-[#0F2A4A]">demand the best.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-slate-600">
              From residential landmarks to banking institutions, Hyderabad's most discerning
              organizations trust STARLINK Security Force with their people, property and events.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-20 lg:py-28" data-testid="clients-page">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {clients.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.1} className="h-full">
                <div
                  data-testid={c.testId}
                  className="h-full rounded-2xl border border-slate-200/80 bg-white px-8 py-12 text-center hover:border-[#E5322D]/40 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_-15px_rgba(229,50,45,0.12)] transition-all duration-300"
                >
                  <span className="mx-auto flex h-20 items-center justify-center">
                    <img src={c.logo} alt={`${c.name} logo`} className="max-h-20 w-auto max-w-full object-contain" />
                  </span>
                  <h3 className="mt-6 text-xl font-extrabold tracking-tight text-slate-900">{c.name}</h3>
                  <p className="mt-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400">
                    {c.sector}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16 text-center">
            <p className="text-base text-slate-600">
              Join Hyderabad's most protected addresses.
            </p>
            <Link
              to="/contact"
              data-testid="clients-cta-button"
              className="group mt-6 inline-flex items-center gap-2 rounded-full bg-[#0F2A4A] px-7 py-3.5 text-sm font-semibold text-white hover:bg-sky-700 transition-colors duration-300"
            >
              Become our next client
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="noise-overlay bg-[#0F2A4A] py-20 lg:py-24" data-testid="testimonials-section">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <span className="text-xs font-mono font-medium tracking-[0.2em] uppercase text-red-400">
            Testimonials
          </span>
          <Quote className="mx-auto mt-6 h-10 w-10 text-red-500/60" />
          <div className="relative mt-8 min-h-[240px] sm:min-h-[190px]">
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={active}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                <p className="font-serif text-xl sm:text-2xl italic leading-relaxed text-white" data-testid="testimonial-quote">
                  "{testimonials[active].quote}"
                </p>
                <footer className="mt-6">
                  <p className="text-sm font-semibold text-white" data-testid="testimonial-author">
                    {testimonials[active].name}
                  </p>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400">
                    {testimonials[active].role}
                  </p>
                </footer>
              </motion.blockquote>
            </AnimatePresence>
          </div>
          <div className="mt-8 flex justify-center gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`Testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === active ? "w-8 bg-red-500" : "w-1.5 bg-white/25 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
